import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

import { apiCall } from '../config/api';
import { useAuth, getStoredUser } from './AuthContext';
import { isThemeDark } from '../config/themes';

export type SidebarState = 'full' | 'collapsed' | 'hidden';

interface StateContextType {
  activeMenu: boolean;
  setActiveMenu: React.Dispatch<React.SetStateAction<boolean>>;
  sidebarState: SidebarState;
  setSidebarState: React.Dispatch<React.SetStateAction<SidebarState>>;
  isClicked: typeof initialState;
  setIsClicked: React.Dispatch<React.SetStateAction<typeof initialState>>;
  handleClick: (clicked: string) => void;
  screenSize: number | undefined;
  setScreenSize: React.Dispatch<React.SetStateAction<number | undefined>>;
  currentTheme: string;
  setCurrentTheme: (value: string) => void;
  currentMode: string;
  isDarkTheme: boolean;
  isAdminUser: boolean;
  isSuperUser: boolean;
  userRoles: string[];
  primaryRole: string | null;
  hasRole: (role: string) => boolean;
  selectedClientId: string;
  setSelectedClientId: React.Dispatch<React.SetStateAction<string>>;
  selectedFacilityId: string;
  setSelectedFacilityId: React.Dispatch<React.SetStateAction<string>>;
  availableClients: Array<{ id: string; name: string }>;
  availableFacilities: Array<{ id: string; name: string; clientId: string }>;
  visibleFacilities: Array<{ id: string; name: string; clientId: string }>;
  selectedClientName: string;
  selectedFacilityName: string;
  themeSettings: boolean;
  setThemeSettings: (value: boolean) => void;
  initialState: typeof initialState;
}

const initialState = {
  help: false,
  settings: false,
  userProfile: false,
  notification: false,
};

const StateContext = createContext<StateContextType | undefined>(undefined);

export const ContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { token } = useAuth();
  const [sidebarState, setSidebarState] = useState<SidebarState>('full');
  const [activeMenu, setActiveMenu] = useState<boolean>(true);
  const [isClicked, setIsClicked] = useState<typeof initialState>(initialState);
  const [screenSize, setScreenSize] = useState<number | undefined>(undefined);
  const [currentTheme, setCurrentThemeState] = useState<string>(localStorage.getItem('theme') || 'ClinicalOutcomes');

  const setCurrentTheme = (theme: string) => {
    localStorage.setItem('theme', theme);
    document.documentElement.setAttribute('data-theme', theme);
    setCurrentThemeState(theme);
  };
  const [selectedClientId, setSelectedClientId] = useState<string>(localStorage.getItem('selected_client_id') || '');
  const [selectedFacilityId, setSelectedFacilityId] = useState<string>(localStorage.getItem('selected_facility_id') || '');
  const [themeSettings, setThemeSettings] = useState<boolean>(false);
  const [availableClients, setAvailableClients] = useState<Array<{ id: string; name: string }>>([]);
  const [availableFacilities, setAvailableFacilities] = useState<Array<{ id: string; name: string; clientId: string }>>([]);

  const visibleFacilities = availableFacilities.filter(f => f.clientId === selectedClientId);
  const selectedClientName = availableClients.find(c => c.id === selectedClientId)?.name || '';
  const selectedFacilityName = availableFacilities.find(f => f.id === selectedFacilityId)?.name || '';

  // Stable reference so it can be called from both the mount effect and the
  // visibilitychange listener without re-registering listeners on every render.
  const loadTenantContext = useCallback(async () => {
    try {
      if (!token) return;

      const response = await apiCall('/core/tenant-context/', { method: 'GET' }, token ?? undefined);
      if (!response.ok) return;

      const data = await response.json();
      const clients: Array<{ id: string; name: string }> = Array.isArray(data.clients)
        ? data.clients.map((item: any) => ({ id: String(item.id), name: String(item.name || '') }))
        : [];
      const facilities: Array<{ id: string; name: string; clientId: string }> = Array.isArray(data.facilities)
        ? data.facilities.map((item: any) => ({
            id: String(item.id),
            name: String(item.name || ''),
            clientId: String(item.clientId || ''),
          }))
        : [];

      setAvailableClients(clients);
      setAvailableFacilities(facilities);

      // Restore saved selection if still valid, otherwise pick the first available.
      // Always call the setters — React ignores no-op updates, and this avoids
      // stale-closure comparisons when the callback is called from the listener.
      const savedClientId = localStorage.getItem('selected_client_id') || '';
      const savedFacilityId = localStorage.getItem('selected_facility_id') || '';

      const validClient = clients.find(c => c.id === savedClientId);
      const resolvedClientId = validClient ? savedClientId : (clients[0]?.id || '');
      setSelectedClientId(resolvedClientId);

      const validFacility = facilities.find(f => f.id === savedFacilityId && f.clientId === resolvedClientId);
      const resolvedFacilityId = validFacility
        ? savedFacilityId
        : (facilities.find(f => f.clientId === resolvedClientId)?.id || '');
      setSelectedFacilityId(resolvedFacilityId);
    } catch {
      // Do nothing — no fallback data, the dropdowns will simply be empty
    }
  }, [token]); // re-runs when token changes (login/logout)

  // Clear tenant data immediately when logged out
  useEffect(() => {
    if (!token) {
      setAvailableClients([]);
      setAvailableFacilities([]);
      setSelectedClientId('');
      setSelectedFacilityId('');
    }
  }, [token]);

  useEffect(() => {
    loadTenantContext();

    // Re-run when the tab becomes visible again — handles browser tab suspension
    // (Chrome Memory Saver / Edge Sleeping Tabs) and OS sleep/wake cycles where
    // the in-memory client+facility state was discarded.
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadTenantContext();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [loadTenantContext]);

  useEffect(() => {
    setActiveMenu(sidebarState !== 'hidden');
  }, [sidebarState]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', currentTheme);
  }, [currentTheme]);

  // Persist client selection and auto-correct facility when client changes
  useEffect(() => {
    if (!selectedClientId) return;
    localStorage.setItem('selected_client_id', selectedClientId);
    localStorage.setItem('selected_client_slug', selectedClientId);

    if (availableFacilities.length === 0) return;
    const facilityBelongsToClient = availableFacilities.some(
      f => f.id === selectedFacilityId && f.clientId === selectedClientId
    );
    if (!facilityBelongsToClient) {
      const first = availableFacilities.find(f => f.clientId === selectedClientId);
      if (first) setSelectedFacilityId(first.id);
    }
  }, [selectedClientId, availableFacilities]);

  useEffect(() => {
    if (!selectedFacilityId) return;
    localStorage.setItem('selected_facility_id', selectedFacilityId);
  }, [selectedFacilityId]);

  const handleClick = (clicked: string) => {
    setIsClicked({ ...initialState, [clicked]: true });
  };

  // TE-63: derived from the theme's actual base-100 luminance (see
  // config/themes.ts :: isThemeDark) rather than a hand-maintained name
  // list, which drifted out of sync (this list only ever knew about
  // TemplarEdgeDark -- every other dark theme, including the new default
  // ClinicalOutcomesDark, was silently treated as light).
  const isDarkTheme = isThemeDark(currentTheme);

  const userData = (() => {
    try {
      return getStoredUser();
    } catch {
      return null;
    }
  })();
  const isAdminUser = !!(
    userData?.is_superuser || userData?.is_staff || userData?.is_admin ||
    userData?.role === 'admin' || userData?.username === 'admin'
  );
  const isSuperUser = !!(userData?.is_superuser);
  const userRoles: string[] = Array.isArray(userData?.roles) ? userData.roles : [];
  const primaryRole: string | null = userData?.primary_role ?? (isSuperUser ? 'global_admin' : null);
  const hasRole = (role: string) => userRoles.includes(role);

  const currentMode = isDarkTheme ? 'Dark' : 'Light';

  return (
    <StateContext.Provider
      value={{
        activeMenu, setActiveMenu,
        sidebarState, setSidebarState,
        isClicked, setIsClicked,
        handleClick,
        screenSize, setScreenSize,
        currentTheme, setCurrentTheme,
        currentMode, isDarkTheme,
        isAdminUser,
        isSuperUser,
        userRoles,
        primaryRole,
        hasRole,
        selectedClientId, setSelectedClientId,
        selectedFacilityId, setSelectedFacilityId,
        availableClients, availableFacilities,
        visibleFacilities,
        selectedClientName, selectedFacilityName,
        themeSettings, setThemeSettings,
        initialState,
      }}
    >
      {children}
    </StateContext.Provider>
  );
};

export const useStateContext = (): StateContextType => {
  const context = useContext(StateContext);
  if (context === undefined) {
    throw new Error('useStateContext must be used within a ContextProvider');
  }
  return context;
};
